// Pantalla de configuración — Reglas de curación GENÉRICAS por tenant (Req. 24.j+).
// Solo propietarios (user_admin). Cada tenant define reglas paramétricas que se
// aplican a nombres de artista/sello/título al crear, editar y consultar: quitar
// paréntesis/corchetes, recortar un sufijo, quitar un texto, o un reemplazo por
// regex (avanzado). Incluye previsualización en vivo contra un nombre de ejemplo.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useApi } from '../../api/ApiContext.js';
import type {
  CurationRule,
  CurationRuleEntity,
  CurationRuleInput,
  CurationRuleKind,
  CurationRuleParams,
} from '../../api/endpoints.js';
import { AlertMessage, Card, Icon, PageHeader, Spinner } from '../../ui/components.js';
import { useAsync } from '../../ui/useAsync.js';

/** ¿El tipo de regla NO lleva parámetros? */
function isParamless(kind: CurationRuleKind): boolean {
  return kind === 'strip_parentheses' || kind === 'strip_brackets';
}

/** Descripción corta del efecto de una regla (para la lista), localizada. */
function describeRule(t: TFunction, rule: CurationRule): string {
  switch (rule.kind) {
    case 'strip_parentheses':
    case 'strip_brackets':
      return t(`curationRules.describe.${rule.kind}`);
    case 'strip_trailing':
    case 'remove_text':
      return t(`curationRules.describe.${rule.kind}`, { value: rule.params.value ?? '' });
    case 'regex_replace':
      return t('curationRules.describe.regex_replace', {
        pattern: rule.params.pattern ?? '',
        flags: rule.params.flags ?? '',
        replacement: rule.params.replacement ?? '',
      });
    default:
      return '';
  }
}

const ENTITIES: CurationRuleEntity[] = ['artist', 'label', 'title'];

export function CurationRulesView() {
  const { t } = useTranslation();
  const api = useApi();
  const rules = useAsync(() => api.config.listCurationRules(), []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Formulario de nueva regla.
  const [form, setForm] = useState<CurationRuleInput>({
    entity_type: 'artist',
    kind: 'strip_parentheses',
    params: {},
    enabled: true,
    priority: 100,
  });

  // Previsualización en vivo contra un nombre de ejemplo.
  const [sample, setSample] = useState('');
  const [preview, setPreview] = useState<string | null>(null);

  const items = rules.data?.items ?? [];
  const byEntity = useMemo(() => {
    const map: Record<CurationRuleEntity, CurationRule[]> = { artist: [], label: [], title: [] };
    for (const r of items) map[r.entity_type]?.push(r);
    for (const e of ENTITIES) map[e].sort((a, b) => a.priority - b.priority);
    return map;
  }, [items]);

  async function run(id: string, fn: () => Promise<unknown>) {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      rules.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('curationRules.errOperation'));
    } finally {
      setBusyId(null);
    }
  }

  async function create() {
    await run('new', async () => {
      await api.config.createCurationRule(form);
      setForm({ ...form, params: paramsForKind(form.kind, {}) });
    });
  }

  // Re-previsualiza al cambiar el ejemplo o la entidad (debounce ligero). La
  // llamada se define dentro del efecto para no depender de identidades externas.
  const previewEntity = form.entity_type;
  useEffect(() => {
    if (sample.trim() === '') {
      setPreview(null);
      return;
    }
    const t = setTimeout(() => {
      api.config
        .previewCurationRules({ sample, entity: previewEntity })
        .then((res) => setPreview(res.output))
        .catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(t);
  }, [api, sample, previewEntity, items]);

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('curationRules.pageTitle')}
        subtitle={t('curationRules.pageSubtitle')}
        back
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <Card title={t('curationRules.howTitle')} icon="auto_fix_high">
          <p className="font-body-sm text-body-sm text-on-surface-variant">
            {t('curationRules.howBody')}
          </p>
        </Card>

        <AlertMessage message={error} />

        {/* Crear regla. */}
        <Card title={t('curationRules.addTitle')} icon="add_circle">
          <RuleForm
            value={form}
            onChange={setForm}
            busy={busyId === 'new'}
            onSubmit={() => void create()}
          />
        </Card>

        {/* Previsualizador en vivo. */}
        <Card title={t('curationRules.tryTitle')} icon="science">
          <div className="flex items-center gap-space-xs">
            <select
              className="field-input h-10 max-w-[120px] bg-surface-deck rounded border border-border-subtle px-2"
              value={form.entity_type}
              onChange={(e) => setForm({ ...form, entity_type: e.target.value as CurationRuleEntity })}
            >
              {ENTITIES.map((en) => (
                <option key={en} value={en}>{t(`curationRules.entity.${en}`)}</option>
              ))}
            </select>
            <input
              className="field-input flex-1 bg-surface-deck rounded border border-border-subtle px-2 h-10"
              placeholder={t('curationRules.samplePlaceholder')}
              value={sample}
              onChange={(e) => setSample(e.target.value)}
            />
          </div>
          {preview !== null && sample.trim() !== '' && (
            <div className="mt-space-sm flex items-center gap-space-xs">
              <span className="font-body-sm text-body-sm text-on-surface-variant line-through truncate max-w-[40%]">
                {sample}
              </span>
              <Icon name="arrow_forward" className="text-[16px] text-primary shrink-0" />
              <span className="font-body-md text-body-md text-vacuum-glow font-bold truncate">
                {preview}
              </span>
            </div>
          )}
        </Card>

        {rules.loading && <Spinner label={t('curationRules.loading')} />}

        {/* Reglas por entidad. */}
        {!rules.loading &&
          ENTITIES.map((en) => (
            <Card key={en} title={t(`curationRules.entity.${en}`)} icon="label">
              {byEntity[en].length === 0 ? (
                <p className="font-body-sm text-body-sm text-on-surface-variant italic">
                  {t('curationRules.noRulesFor', { entity: t(`curationRules.entity.${en}`).toLowerCase() })}
                </p>
              ) : (
                <div className="flex flex-col gap-space-xs">
                  {byEntity[en].map((r, idx) => (
                    <RuleRow
                      key={r.id}
                      rule={r}
                      busy={busyId === r.id}
                      isFirst={idx === 0}
                      isLast={idx === byEntity[en].length - 1}
                      onToggle={() => run(r.id, () => api.config.updateCurationRule(r.id, { enabled: !r.enabled }))}
                      onDelete={() => run(r.id, () => api.config.deleteCurationRule(r.id))}
                      onMove={(dir) => {
                        const list = byEntity[en];
                        const swapWith = dir === 'up' ? list[idx - 1] : list[idx + 1];
                        if (!swapWith) return;
                        // Intercambia prioridades con el vecino.
                        void run(r.id, async () => {
                          await api.config.updateCurationRule(r.id, { priority: swapWith.priority });
                          await api.config.updateCurationRule(swapWith.id, { priority: r.priority });
                        });
                      }}
                    />
                  ))}
                </div>
              )}
            </Card>
          ))}
      </div>
    </div>
  );
}

/** Devuelve los `params` por defecto al cambiar de tipo de regla. */
function paramsForKind(kind: CurationRuleKind, prev: CurationRuleParams): CurationRuleParams {
  switch (kind) {
    case 'strip_parentheses':
    case 'strip_brackets':
      return {};
    case 'strip_trailing':
    case 'remove_text':
      return { value: prev.value ?? '' };
    case 'regex_replace':
      return { pattern: prev.pattern ?? '', flags: prev.flags ?? '', replacement: prev.replacement ?? '' };
    default:
      return {};
  }
}

/** Formulario de una regla (crear). Expone regex solo en modo avanzado. */
function RuleForm({
  value,
  onChange,
  busy,
  onSubmit,
}: {
  value: CurationRuleInput;
  onChange: (v: CurationRuleInput) => void;
  busy: boolean;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  const setKind = (kind: CurationRuleKind) =>
    onChange({ ...value, kind, params: paramsForKind(kind, value.params) });

  const canSubmit =
    isParamless(value.kind) ||
    (value.kind === 'regex_replace'
      ? (value.params.pattern ?? '').trim() !== ''
      : (value.params.value ?? '').trim() !== '');

  return (
    <div className="flex flex-col gap-space-sm">
      <div className="flex items-center gap-space-xs flex-wrap">
        <select
          className="field-input h-10 max-w-[120px] bg-surface-deck rounded border border-border-subtle px-2"
          value={value.entity_type}
          onChange={(e) => onChange({ ...value, entity_type: e.target.value as CurationRuleEntity })}
        >
          {ENTITIES.map((en) => (
            <option key={en} value={en}>{t(`curationRules.entity.${en}`)}</option>
          ))}
        </select>
        <select
          className="field-input flex-1 min-w-[200px] bg-surface-deck rounded border border-border-subtle px-2 h-10"
          value={value.kind}
          onChange={(e) => setKind(e.target.value as CurationRuleKind)}
        >
          {(['strip_parentheses', 'strip_brackets', 'strip_trailing', 'remove_text', 'regex_replace'] as CurationRuleKind[]).map((k) => (
            <option key={k} value={k}>{t(`curationRules.kind.${k}`)}</option>
          ))}
        </select>
      </div>

      {/* Parámetros según el tipo. */}
      {(value.kind === 'strip_trailing' || value.kind === 'remove_text') && (
        <input
          className="field-input bg-surface-deck rounded border border-border-subtle px-2 h-10"
          placeholder={value.kind === 'strip_trailing' ? t('curationRules.trailingPlaceholder') : t('curationRules.removeTextPlaceholder')}
          value={value.params.value ?? ''}
          onChange={(e) => onChange({ ...value, params: { ...value.params, value: e.target.value } })}
        />
      )}

      {value.kind === 'regex_replace' && (
        <div className="flex flex-col gap-space-xs rounded-lg border border-goldmine-vg/40 bg-goldmine-vg/10 p-space-sm">
          <p className="font-badge-micro text-badge-micro text-goldmine-vg uppercase tracking-wide">
            {t('curationRules.advancedRegex')}
          </p>
          <input
            className="field-input bg-surface-deck rounded border border-border-subtle px-2 h-10 font-mono"
            placeholder={t('curationRules.patternPlaceholder')}
            value={value.params.pattern ?? ''}
            onChange={(e) => onChange({ ...value, params: { ...value.params, pattern: e.target.value } })}
          />
          <div className="flex items-center gap-space-xs">
            <input
              className="field-input max-w-[90px] bg-surface-deck rounded border border-border-subtle px-2 h-10 font-mono"
              placeholder={t('curationRules.flagsPlaceholder')}
              value={value.params.flags ?? ''}
              onChange={(e) => onChange({ ...value, params: { ...value.params, flags: e.target.value } })}
            />
            <input
              className="field-input flex-1 bg-surface-deck rounded border border-border-subtle px-2 h-10 font-mono"
              placeholder={t('curationRules.replacementPlaceholder')}
              value={value.params.replacement ?? ''}
              onChange={(e) => onChange({ ...value, params: { ...value.params, replacement: e.target.value } })}
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-space-xs">
        <label className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface-variant">
          <span>{t('curationRules.priority')}</span>
          <input
            type="number"
            className="field-input w-20 bg-surface-deck rounded border border-border-subtle px-2 h-9"
            value={value.priority ?? 100}
            onChange={(e) => onChange({ ...value, priority: Number(e.target.value) })}
          />
        </label>
        <button className="btn-primary" type="button" disabled={busy || !canSubmit} onClick={onSubmit}>
          <Icon name="add" className="text-[18px]" /> {t('curationRules.addRule')}
        </button>
      </div>
    </div>
  );
}

/** Una fila de regla con acciones (activar, mover, eliminar). */
function RuleRow({
  rule,
  busy,
  isFirst,
  isLast,
  onToggle,
  onDelete,
  onMove,
}: {
  rule: CurationRule;
  busy: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onMove: (dir: 'up' | 'down') => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={`flex items-center gap-space-xs surface-rack-card !p-space-sm ${rule.enabled ? '' : 'opacity-50'}`}>
      <div className="flex flex-col shrink-0">
        <button
          className="text-on-surface-variant disabled:opacity-30 hover:text-on-surface"
          type="button"
          disabled={busy || isFirst}
          onClick={() => onMove('up')}
          title={t('curationRules.moveUp')}
        >
          <Icon name="keyboard_arrow_up" className="text-[16px]" />
        </button>
        <button
          className="text-on-surface-variant disabled:opacity-30 hover:text-on-surface"
          type="button"
          disabled={busy || isLast}
          onClick={() => onMove('down')}
          title={t('curationRules.moveDown')}
        >
          <Icon name="keyboard_arrow_down" className="text-[16px]" />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body-sm text-body-sm text-on-surface truncate">{t(`curationRules.kind.${rule.kind}`)}</p>
        <p className="font-body-sm text-body-sm text-on-surface-variant truncate">{describeRule(t, rule)}</p>
      </div>
      <button
        className={`btn-sm shrink-0 ${rule.enabled ? 'btn-secondary' : 'btn-ghost'}`}
        type="button"
        disabled={busy}
        onClick={onToggle}
        title={rule.enabled ? t('curationRules.deactivate') : t('curationRules.activate')}
      >
        <Icon name={rule.enabled ? 'toggle_on' : 'toggle_off'} className="text-[18px]" />
      </button>
      <button
        className="btn-ghost btn-sm shrink-0 text-error"
        type="button"
        disabled={busy}
        onClick={onDelete}
        title={t('curationRules.delete')}
      >
        <Icon name="delete" className="text-[16px]" />
      </button>
    </div>
  );
}
